import { useTranslation } from "react-i18next";

import { FormContainer, Treturn, useFetch } from "@4uhub/lib4uhub";
import { memo, useCallback } from "react";
import { AutomationRuleEncMessageFields } from "./AutomationRuleEncMessageFields";
import {
  automationRuleEncMessageSchema,
  TAutomationRuleEncMessageForm,
} from "./automationRuleEncMessageSchema";
import {
  createTicketAutomationRuleEncMessage,
  getTicketAutomationRuleEncMessageById,
  updateTicketAutomationRuleEncMessageById,
} from "../../../../../../../services/ticketAutomationRuleEncMessage.service";
import { IAutomationRuleEncMessageProps } from "./models";

const translationPath = "chatBot.page.automations.automationRuleEncMessage.";

export const AutomationRuleEncMessage = memo(
  ({
    id,
    ticketAutomationRuleId,
    ticketAutomationTemplateId,
    onBackButton,
  }: IAutomationRuleEncMessageProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createTicketAutomationRuleEncMessage,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateTicketAutomationRuleEncMessageById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getTicketAutomationRuleEncMessageById,
    );

    const saveHandler = (v: TAutomationRuleEncMessageForm) => {
      return save({
        ...v,
        ticketAutomationRuleId: ticketAutomationRuleId ?? "",
        priority: Number(v.priority),
        qtyMessageSendingAttempts: Number(v.qtyMessageSendingAttempts),
        individualIdentifier: v.individualIdentifier
          ? v.individualIdentifier
          : null,
        initialDate: v.initialDate?.toISOString().substring(0, 10),
        endDate: v.endDate?.toISOString().substring(0, 10),
        establishments: v.establishments?.map((e) => e.id),
        ticketAutomationTemplateId: ticketAutomationTemplateId,
        templateGeneralId: v.templateGeneral?.id,
        sendTypeEmrResultId: v.sendTypeEmrResult?.id,
        sendTypePasswordEmrResultId: v.sendTypePasswordEmrResult?.id,
        ticketAutomationRuleEncMessageSectors:
          v.ticketAutomationRuleEncMessageSectors?.map((e) => ({
            externalId: e.id,
            externalName: e.name,
          })),
        ticketAutomationRuleEncMessagePrescriptionSectors:
          v.ticketAutomationRuleEncMessagePrescriptionSectors?.map((e) => ({
            externalId: e.id,
            externalName: e.name,
          })),
        ticketAutomationRuleEncMessageMedProcTypes:
          v.ticketAutomationRuleEncMessageMedProcTypes?.map((e) => ({
            externalId: e.id,
            externalName: e.name,
          })),
        ticketAutomationRuleEncMessageEncounterTypes:
          v.ticketAutomationRuleEncMessageEncounterTypes?.map((e) => ({
            externalId: e.id,
            externalName: e.name,
          })),
        ticketAutomationRuleEncMessageInProcClassifics:
          v.ticketAutomationRuleEncMessageInProcClassifics?.map((e) => ({
            externalId: e.id,
            externalName: e.name,
          })),
      });
    };

    const updateHandler = (v: TAutomationRuleEncMessageForm, id: string) =>
      update({
        item: {
          id: id,
          ...v,
          ticketAutomationRuleId: ticketAutomationRuleId ?? "",
          priority: Number(v.priority),
          qtyMessageSendingAttempts: Number(v.qtyMessageSendingAttempts),
          individualIdentifier: v.individualIdentifier
            ? v.individualIdentifier
            : null,
          initialDate: v.initialDate?.toISOString().substring(0, 10),
          endDate: v.endDate?.toISOString().substring(0, 10),
          establishments: v.establishments?.map((e) => e.id),
          ticketAutomationTemplateId: ticketAutomationTemplateId,
          templateGeneralId: v.templateGeneral?.id,
          sendTypeEmrResultId: v.sendTypeEmrResult?.id,
          sendTypePasswordEmrResultId: v.sendTypePasswordEmrResult?.id,
          ticketAutomationRuleEncMessageSectors:
            v.ticketAutomationRuleEncMessageSectors?.map((e) => ({
              externalId: e.id,
              externalName: e.name,
            })),
          ticketAutomationRuleEncMessagePrescriptionSectors:
            v.ticketAutomationRuleEncMessagePrescriptionSectors?.map((e) => ({
              externalId: e.id,
              externalName: e.name,
            })),
          ticketAutomationRuleEncMessageMedProcTypes:
            v.ticketAutomationRuleEncMessageMedProcTypes?.map((e) => ({
              externalId: e.id,
              externalName: e.name,
            })),
          ticketAutomationRuleEncMessageEncounterTypes:
            v.ticketAutomationRuleEncMessageEncounterTypes?.map((e) => ({
              externalId: e.id,
              externalName: e.name,
            })),
          ticketAutomationRuleEncMessageInProcClassifics:
            v.ticketAutomationRuleEncMessageInProcClassifics?.map((e) => ({
              externalId: e.id,
              externalName: e.name,
            })),
        },
        id: id,
      });

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TAutomationRuleEncMessageForm>> => {
        const { data, success } = await get(id);

        if (data) {
          const newData: TAutomationRuleEncMessageForm = {
            ...data,
            priority: data.priority?.toString(),
            qtyMessageSendingAttempts:
              data.qtyMessageSendingAttempts?.toString(),
            individualIdentifier: data.individualIdentifier
              ? data.individualIdentifier
              : null,
            establishments: data.establishments?.map((e) => ({
              id: e.id,
              name: e.name,
            })),
            initialDate: new Date(`${data.initialDate}T12:00:00Z`),
            endDate: new Date(`${data.endDate}T12:00:00Z`),
            templateGeneral: data.templateGeneral
              ? {
                  id: data.templateGeneral.id,
                  name: data.templateGeneral.name,
                }
              : null,
            ticketAutomationRuleEncMessageSectors: data.sectors?.map((e) => ({
              id: e.externalId,
              name: e.externalName,
            })),
            ticketAutomationRuleEncMessagePrescriptionSectors:
              data.prescriptionSectors?.map((e) => ({
                id: e.externalId,
                name: e.externalName,
              })),
            ticketAutomationRuleEncMessageMedProcTypes: data.medProcTypes?.map(
              (e) => ({
                id: e.externalId,
                name: e.externalName,
              }),
            ),
            ticketAutomationRuleEncMessageEncounterTypes:
              data.encounterTypes?.map((e) => ({
                id: e.externalId,
                name: e.externalName,
              })),
            ticketAutomationRuleEncMessageInProcClassifics:
              data.inProcClassifics?.map((e) => ({
                id: e.externalId,
                name: e.externalName,
              })),
          };
          return { data: newData, success: true };
        }
        return { data, success };
      },
      [get],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TAutomationRuleEncMessageForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "message")}
        schema={automationRuleEncMessageSchema}
        subtitle={t(translationPath + "new_message")}
        subtitleWatchField={"name"}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        defaultValues={{
          exception: false,
        }}
        fields={() => <AutomationRuleEncMessageFields idMessage={id} />}
      />
    );
  },
);
